import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseUpstreamDatasourcesFilteredByTableQuery,
  DatabaseUpstreamDatasourcesFilteredByTableQueryVariables
} from '../../../gen-src/queries/DatabaseUpstreamDatasourcesFilteredByTableQuery';
import {
  DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQuery,
  DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQuery';
import {
  DatabaseUpstreamDatasourcesQuery,
  DatabaseUpstreamDatasourcesQueryVariables,
} from '../../../gen-src/queries/DatabaseUpstreamDatasourcesQuery';
import {
  DatabaseUpstreamDatasourcesWithoutPersonalSpaceQuery,
  DatabaseUpstreamDatasourcesWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseUpstreamDatasourcesWithoutPersonalSpaceQuery';
import { DatasourcesDisplayGridRowDataFragment } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragment';
import { DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace';
import { PublishedDatasourceSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseUpstreamDatasourcesFilteredByTableQueryGql } from '../../queries/DatabaseUpstreamDatasourcesFilteredByTableQueryGql';
import { DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryGql';
import { DatabaseUpstreamDatasourcesQueryGql } from '../../queries/DatabaseUpstreamDatasourcesQueryGql';
import { DatabaseUpstreamDatasourcesWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseUpstreamDatasourcesWithoutPersonalSpaceQueryGql';
import {
  DatasourcesDisplayGrid,
  DatasourcesDisplayGridRowData,
  getRowDataForDatasource,
  getRowDataForDatasourceWithoutPersonalSpace,
} from '../sharedDisplayGrid/DatasourcesDisplayGrid';
import { DatasourcesValidSortFields } from '../sorting/sortProviders/DatasourcesGridSortProvider';

type QueryVariables = DatabaseUpstreamDatasourcesQueryVariables;
type QueryResults = DatabaseUpstreamDatasourcesQuery;
type QueryResultListNode = DatasourcesDisplayGridRowDataFragment;
type GridRowData = DatasourcesDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatabaseUpstreamDatasourcesWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatabaseUpstreamDatasourcesWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = DatasourcesDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DatabaseUpstreamDatasourcesFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseUpstreamDatasourcesFilteredByTableQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].upstreamDatasourcesConnection;
}

function selectConnectionListResultWithoutPersonalSpace(
  resultData: QueryResultsWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].upstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].upstreamDatasourcesConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(
  resultData: QueryResultsWithFilterWithoutPersonalSpace
): ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].upstreamDatasourcesConnection;
}

export type DatabaseUpstreamDatasourcesQueryGridProps =
  GridSortProps<DatasourcesValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseUpstreamDatasourcesQueryGrid(props: DatabaseUpstreamDatasourcesQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamDatasourcesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredDatasourcesLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DatabaseUpstreamDatasourcesFilteredByTableQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseUpstreamDatasourcesQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDatasource}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseUpstreamDatasourcesFilteredByTableWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseUpstreamDatasourcesWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as PublishedDatasourceSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForDatasourceWithoutPersonalSpace}
      >
        {({ rowStore }) =>
          <DatasourcesDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        }
      </PaginatedQueryWrapper>
    )
  );
}
